import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/destination.css';
import axiosInstance from '../../../axiosConfig';

const normalizeCode = (value) => (value || "").toString().toUpperCase();

const DestinationContactForm = ({ destinationUuid, isViewMode }) => {
  const [activeTab, setActiveTab] = useState('Contact Info');
  const [contactType, setcontactType] = useState('primary');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const navigate = useNavigate();

  // Initialize formData state BEFORE useEffect
  const [formData, setFormData] = useState({
    destinationUuid: destinationUuid,
    contacts: [
      { id: 12, typeCode: "PRIMARY", contactNo: "" },
      { id: 13, typeCode: "SECONDARY", contactNo: "" }
    ],
    emails: [
      { id: 1, typeCode: "PRIMARY", email: "" }
    ],
    socialMedias: [
      { id: 1, typeCode: "FACEBOOK", accessLink: "" }
    ],
    specialHotline: "",
    website: "",
    openingHours: [],
    isEntranceFree: 1
  });

  // Scroll to top when component loads
  useEffect(() => {
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    // Fetch saved contact data
    const fetchContactData = async () => {
      if (destinationUuid) {
        try {
          const response = await axiosInstance.get(
            `/internal-api/v1/client-web/destination-panel/config/${destinationUuid}/view`
          );
          if (response.data && response.data.result) {
            const data = response.data.result;
            const contactsFromApi = data.contacts?.map(c => ({
              id: c.contactType?.id || 0,
              typeCode: normalizeCode(c.contactType?.code),
              contactNo: c.contactNo || ""
            })) || [];

            const normalizedContacts = [
              contactsFromApi.find(c => c.typeCode === "PRIMARY") || { id: 12, typeCode: "PRIMARY", contactNo: "" },
              contactsFromApi.find(c => c.typeCode === "SECONDARY") || { id: 13, typeCode: "SECONDARY", contactNo: "" }
            ];

            const emailsFromApi = data.emails?.map(e => ({
              id: e.emailType?.id || 0,
              typeCode: normalizeCode(e.emailType?.code || "PRIMARY"),
              email: e.email || ""
            })) || [];

            const normalizedEmails = [
              emailsFromApi.find(e => e.typeCode === "PRIMARY") || { id: 1, typeCode: "PRIMARY", email: "" }
            ];

            const mediasFromApi = data.medias?.map(m => ({
              id: m.media?.id || 0,
              typeCode: normalizeCode(m.media?.code || "FACEBOOK"),
              accessLink: m.accessUrl || ""
            })) || [];

            const normalizedSocialMedias = [
              mediasFromApi.find(m => m.typeCode === "FACEBOOK") || { id: 1, typeCode: "FACEBOOK", accessLink: "" }
            ];

            setFormData({
              destinationUuid: destinationUuid,
              contacts: normalizedContacts,
              emails: normalizedEmails,
              socialMedias: normalizedSocialMedias,
              specialHotline: data.detail?.specialHotline || "",
              website: data.detail?.website || "",
              openingHours: data.detail?.openingHours || [],
              isEntranceFree: data.detail?.isEntranceFree !== undefined ? data.detail.isEntranceFree : 1
            });
            setIsReadOnly(isViewMode);
          }
        } catch (error) {
          console.error("Error fetching contact data:", error);
        }
      }
    };
    
    fetchContactData();
  }, [destinationUuid, isViewMode]);

  useEffect(() => {
    if (isViewMode) {
      setIsReadOnly(true);
    }
  }, [isViewMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => {
      const updatedData = { ...prevData };
      const keys = name.split(/[\.\[\]]/).filter(Boolean);
      let current = updatedData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = isNaN(keys[i]) ? keys[i] : parseInt(keys[i], 10);
        current = current[key];
      }

      const lastKey = isNaN(keys[keys.length - 1]) ? keys[keys.length - 1] : parseInt(keys[keys.length - 1], 10);
      current[lastKey] = value;

      return updatedData;
    });
  };
  const handleAddOpeningHour = () => {
    setFormData(prev => ({
      ...prev,
      openingHours: [...prev.openingHours, { day: "Monday", from: "09:00", to: "17:00" }]
    }));
  };

  const handleRemoveOpeningHour = (index) => {
    setFormData(prev => ({
      ...prev,
      openingHours: prev.openingHours.filter((_, i) => i !== index)
    }));
  };
  // ✅ UPDATED: Show message and reload page
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const primaryEmail = formData.emails.find(email => email.typeCode === "PRIMARY");
    const hasPrimaryEmail = primaryEmail && primaryEmail.email && primaryEmail.email.trim() !== '';
    
    if (!hasPrimaryEmail) {
      alert('Primary Email is required!');
      return;
    }
    
    // Build the payload with spaces for empty values
    const payload = {
      destinationUuid: formData.destinationUuid,
      isEntranceFree: formData.isEntranceFree
    };
    
    // Add emails - map all, use space if empty
    payload.emails = formData.emails.map(email => ({
      ...email,
      email: email.email && email.email.trim() !== '' ? email.email : ' '
    }));
    
    // Add contacts - map all, use space if empty
    payload.contacts = formData.contacts.map(contact => ({
      ...contact,
      contactNo: contact.contactNo && contact.contactNo.trim() !== '' ? contact.contactNo : ' '
    }));
    
    // Add socialMedias - map all, use space if empty
    payload.socialMedias = formData.socialMedias.map(sm => ({
      ...sm,
      accessLink: sm.accessLink && sm.accessLink.trim() !== '' ? sm.accessLink : ' '
    }));
    
    // Add specialHotline - use space if empty
    payload.specialHotline = formData.specialHotline && formData.specialHotline.trim() !== '' 
      ? formData.specialHotline 
      : ' ';
    
    // Add website - use space if empty
    payload.website = formData.website && formData.website.trim() !== '' 
      ? formData.website 
      : ' ';
    
    // Add openingHours - map all, use space if empty times
    payload.openingHours = formData.openingHours.map(hours => ({
      day: hours.day,
      from: hours.from || ' ',
      to: hours.to || ' '
    }));
    
    console.log("Form Submitted:", JSON.stringify(payload, null, 2));
    
    try {
      const response = await axiosInstance.post(
        '/internal-api/v1/client-web/destination-panel/registration/destination-contacts',
        payload,
      );

      if (response.data && response.data.result) {
        console.log("Form submitted successfully:", response.data.result);
        alert('Destination information saved successfully!');
        navigate('/DestinationList');
      } else {
        console.error("Error submitting form:", response);
        alert('Form submitted but no result returned. Please check the data.');
      }

    } catch (error) {
      console.error("Error submitting form:", error);
      console.error("Error response:", error.response?.data);
      
      let errorMessage = 'Error submitting form. ';
      if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error.message) {
        errorMessage += error.message;
      }
      alert(errorMessage);
    }
  };

  return (
    <div className="text-sm">
      {!destinationUuid ? (
        <div className="p-6">
          <div className="p-6 text-center rounded bg-blue-50">
            <h3 className="mb-3 text-base font-medium text-gray-700">Contact Information</h3>
            <p className="mb-2 text-sm text-gray-600">Please complete the Basic Info step first to enable contact management.</p>
            <p className="mt-3 text-xs text-gray-600">After saving the destination, you will be able to add contact information with:</p>
            <p className="mt-2 text-xs text-gray-600">
              • Primary and secondary contacts • Email addresses • Social media links • Opening hours • Entrance fee details
            </p>
          </div>
        </div>
      ) : (
      <div className="p-6 content">
        <h2 className="mb-4 text-base font-medium text-slate-700">Contact Information</h2>

        <div className="mt-6">
          <h3 className="mb-1 text-base font-medium text-gray-800">Contact Details</h3>
          <p className="mb-4 text-xs text-gray-600">Add contact information for visitors to reach this destination</p>

          {/* Phone Numbers */}
          {/* <div className="mb-6">
            <h4 className="mb-2 text-sm font-medium text-gray-700">Phone Number</h4>
            <div className="flex items-center mb-2">
              <select value={contactType} onChange={e => setcontactType(e.target.value)} className="px-4 py-2 text-xs border border-gray-300 rounded-l w-36">
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
              </select>
              <input
                type="text"
                name={`contacts[${contactType === 'primary' ? 0 : 1}].contactNo`}
                value={formData.contacts[contactType === 'primary' ? 0 : 1].contactNo}
                onChange={handleChange}
                placeholder="Enter Phone Number"
                className="flex-grow px-4 py-2 text-xs border border-l-0 border-gray-300 rounded-r"
              />
            </div>
          </div> */}
              <div className="mb-6">
            <h4 className="mb-2 text-sm font-medium text-gray-700">Contact No</h4>
            <div className="flex items-center mb-2">
              <input type="text" value="Primary" className="px-4 py-2 text-xs border border-gray-300 rounded-l w-36 bg-gray-50" readOnly />
              <input
                type="text"
                name="contacts[0].contactNo"
                value={formData.contacts[0].contactNo}
                onChange={handleChange}
                placeholder="Enter Contact No"
                className="flex-grow px-4 py-2 text-xs border border-l-0 border-gray-300 rounded-r"
                disabled={isReadOnly}
              />
            </div>
          </div>
              <div className="mb-6">
            {/* <h4 className="mb-2 text-sm font-medium text-gray-700">Contact No</h4> */}
            <div className="flex items-center mb-2">
              <input type="text" value="Secondary" className="px-4 py-2 text-xs border border-gray-300 rounded-l w-36 bg-gray-50" readOnly />
              <input
                type="text"
                name="contacts[1].contactNo"
                value={formData.contacts[1].contactNo}
                onChange={handleChange}
                placeholder="Enter Contact No"
                className="flex-grow px-4 py-2 text-xs border border-l-0 border-gray-300 rounded-r"
                disabled={isReadOnly}
              />
            </div>
          </div>

          {/* Email Address */}
          <div className="mb-6">
            <h4 className="mb-2 text-sm font-medium text-gray-700">Email <span className="text-red-600">*</span></h4>
            <div className="flex items-center mb-2">
              <input type="text" value="Primary" className="px-4 py-2 text-xs border border-gray-300 rounded-l w-36 bg-gray-50" readOnly />
              <input
                type="email"
                name="emails[0].email"
                value={formData.emails[0].email}
                onChange={handleChange}
                placeholder="Enter Email Address"
                className="flex-grow px-4 py-2 text-xs border border-l-0 border-gray-300 rounded-r"
                required
                disabled={isReadOnly}
              />
            </div>
          </div>

          {/* Special Hotline */}
          <div className="mb-6">
            <h4 className="mb-2 text-sm font-medium text-gray-700">Special Hotline</h4>
            <input
              type="tel"
              name="specialHotline"
              value={formData.specialHotline}
              onChange={handleChange}
              placeholder="Enter special hotline number (e.g. +1-800-999-8888)"
              className="w-full px-4 py-2 text-xs border border-gray-300 rounded"
              disabled={isReadOnly}
            />
          </div>

          {/* Website */}
          <div className="mb-6">
            <h4 className="mb-2 text-sm font-medium text-gray-700">Website</h4>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="Enter website URL (e.g. https://example.com)"
              className="w-full px-4 py-2 text-xs border border-gray-300 rounded"
              disabled={isReadOnly}
            />
          </div>

          {/* Social Media */}
          <div className="mb-6">
            <h4 className="mb-2 text-sm font-medium text-gray-700">Social Media</h4>
            <div className="flex items-center mb-2">
              <input type="text" value="Facebook" className="px-4 py-2 text-xs border border-gray-300 rounded-l w-36 bg-gray-50" readOnly />
              <input
                type="url"
                name="socialMedias[0].accessLink"
                value={formData.socialMedias[0].accessLink}
                onChange={handleChange}
                placeholder="Enter Facebook page URL"
                className="flex-grow px-4 py-2 text-xs border border-l-0 border-gray-300 rounded-r"
                disabled={isReadOnly}
              />
            </div>
          </div>

          {/* Opening Hours */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">Opening Hours</h4>
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={handleAddOpeningHour}
                  className="px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                  + Add Opening Hours
                </button>
              )}
            </div>
            {formData.openingHours.map((hours, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <select
                  name={`openingHours[${index}].day`}
                  value={hours.day}
                  onChange={handleChange}
                  className="w-32 px-4 py-2 text-xs border border-gray-300 rounded"
                  disabled={isReadOnly}
                >
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </select>
                <input
                  type="time"
                  name={`openingHours[${index}].from`}
                  value={hours.from}
                  onChange={handleChange}
                  className="px-4 py-2 text-xs border border-gray-300 rounded"
                  disabled={isReadOnly}
                />
                <span className="text-gray-500">to</span>
                <input
                  type="time"
                  name={`openingHours[${index}].to`}
                  value={hours.to}
                  onChange={handleChange}
                  className="px-4 py-2 text-xs border border-gray-300 rounded"
                  disabled={isReadOnly}
                />
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => handleRemoveOpeningHour(index)}
                    className="px-2 py-1 text-xs text-red-600 border border-red-600 rounded hover:bg-red-50"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            {formData.openingHours.length === 0 && (
              <p className="text-xs text-gray-500">No opening hours added yet. Click "Add Opening Hours" to add.</p>
            )}
          </div>

          {/* Entrance Fee */}
          <div className="mb-6">
            <h4 className="mb-2 text-sm font-medium text-gray-700">Entrance <span className="text-red-600">*</span></h4>
            <div className="flex items-center gap-4">
              <label className="flex items-center" style={{cursor: isReadOnly ? 'not-allowed' : 'pointer'}}>
                <input
                  type="radio"
                  name="isEntranceFree"
                  value="1"
                  checked={formData.isEntranceFree === 1}
                  onChange={(e) => setFormData({...formData, isEntranceFree: parseInt(e.target.value)})}
                  className="mr-2"
                  disabled={isReadOnly}
                />
                <span className="text-xs">Free Entrance</span>
              </label>
              <label className="flex items-center" style={{cursor: isReadOnly ? 'not-allowed' : 'pointer'}}>
                <input
                  type="radio"
                  name="isEntranceFree"
                  value="0"
                  checked={formData.isEntranceFree === 0}
                  onChange={(e) => setFormData({...formData, isEntranceFree: parseInt(e.target.value)})}
                  className="mr-2"
                  disabled={isReadOnly}
                />
                <span className="text-xs">Paid Entrance</span>
              </label>
            </div>
          </div>
        </div>

        {!isReadOnly && (
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              className="px-6 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default DestinationContactForm;
